import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import { Skeleton } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../../components/main-heading.component";
import { _get } from "../../../services/api.service";
import "../content-engine.css";
import { contentEngineService } from "../content-engine.service";
import "../../video-create/CreateVideo.css";
import "./BrowseByNiche.css";

interface Niche {
  name: string;
  display_name: string;
  display_order: number;
}

interface Topic {
  id?: number;
  name: string;
}

const BrowseByNiche: React.FC = () => {
  const navigate = useNavigate();
  const steps = [
    { label: "Topic", description: "Enter your topic idea", path: "/content-engine" },
    { label: "Format", description: "Choose your content format", path: "/content-engine/select-format" },
    { label: "Scripts", description: "Pick from generated scripts", path: "/content-engine/choose-script" },
    { label: "Refinement", description: "Refine and finalize your script", path: "/content-engine/script-detail" },
  ];
  const currentStep = 0;
  const [niches, setNiches] = useState<Niche[]>([]);
  const [loading, setLoading] = useState(true);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [selectedNiche, setSelectedNiche] = useState("");
  const [search, setSearch] = useState("");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [shuffledTopics, setShuffledTopics] = useState<Topic[]>([]);
  const fetched = useRef(false);
  const nicheFetched = useRef("");

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const response = await _get("content-engine/niches/", {}, true);
        const sortedNiches = (response || []).sort(
          (a: Niche, b: Niche) => a.display_order - b.display_order
        );
        setNiches(sortedNiches);
      } catch (error) {
        console.error("Error fetching niches", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedNiche) return;

    nicheFetched.current = "";
    setTopics([]);
    setShuffledTopics([]);
    setSearch("");

    (async () => {
      setTopicsLoading(true);
      try {
        const response = await _get(`content-engine/niches/${selectedNiche}/topics/`, {}, true);
        const formattedTopics = (response || []).map((item: any, index: number) => ({
          id: item.id || index,
          name: item.name || item.topic_text || item.title || item.topic || "",
        }));
        setTopics(formattedTopics);
      } catch (error) {
        console.error("Error fetching topics", error);
      } finally {
        setTopicsLoading(false);
      }
    })();
  }, [selectedNiche]);

  const filteredTopics = useMemo(() => {
    const currentTopics = shuffledTopics.length > 0 ? shuffledTopics : topics;
    return currentTopics
      .filter((item) => item.name.toLowerCase().includes(search.toLowerCase()))
      .slice(0, 12);
  }, [topics, search, shuffledTopics]);

  const shuffleTopics = () => {
    setShuffledTopics([...topics].sort(() => Math.random() - 0.5));
  };

  const handleTopicClick = (topic: Topic) => {
    contentEngineService.setSelectedTopic(topic.id!, topic.name);
    navigate("/content-engine/select-format");
  };

  const selectedNicheLabel =
    niches.find((n) => n.name === selectedNiche)?.display_name || selectedNiche;

  return (
    <div className="container-fluid browse-by-niche-page">
      <MainHeading
        title="Browse by Niche"
        subTitle="Select your niche and pick a proven topic starter"
      />

      <div className="cv-workspace">
        <aside className="cv-progress-panel" aria-label="Content engine progress">
          <div className="cv-progress-caption">CONTENT ENGINE</div>
          {steps.map((step, index) => {
            const complete = index < currentStep;
            const active = index === currentStep;
            return (
              <div
                key={step.label}
                className={`cv-progress-step${complete ? " complete" : ""}${active ? " active" : ""}${complete ? " clickable" : ""}`}
                onClick={() => complete && navigate(step.path)}
                onKeyDown={(e) => complete && e.key === "Enter" && navigate(step.path)}
                role={complete ? "button" : undefined}
                tabIndex={complete ? 0 : undefined}
              >
                <div className="cv-progress-rail">
                  <span>{complete ? "✓" : index + 1}</span>
                </div>
                <div>
                  <div className="cv-progress-title">{step.label}</div>
                  <div className="cv-progress-description">{step.description}</div>
                </div>
              </div>
            );
          })}
        </aside>

        <button
          type="button"
          className="bbn-back-link ce-mobile-nav-link"
          onClick={() => navigate("/content-engine")}
        >
          ← Back to Topics
        </button>

        <section className="cv-creator-card">
          <div className="cv-card-inner">
            <div className="bbn-head">
              <p className="subtitle">
                <CategoryOutlinedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-top", color: "#2364e8" }} />
                Choose a niche to explore topic ideas
              </p>
              <button
                type="button"
                className="bbn-back-link ce-desktop-nav-link"
                onClick={() => navigate("/content-engine")}
              >
                ← Back to Topics
              </button>
            </div>

            <div className="niche-grid">
              {loading
                ? [1, 2, 3, 4, 5, 6, 8].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: "10px" }} />
                  ))
                : niches.map((niche) => (
                    <button
                      key={niche.name}
                      type="button"
                      className={`niche${selectedNiche === niche.name ? " active" : ""}`}
                      title={niche.display_name}
                      onClick={() => setSelectedNiche(niche.name)}
                    >
                      {niche.display_name}
                    </button>
                  ))}
            </div>

            {selectedNiche && (
              <div className="topics-section">
                <div className="topics-header">
                  <div className="bbn-selected-label">
                    Topics in <strong>{selectedNicheLabel}</strong>
                  </div>
                  <div className="topics-header-actions">
                    <div className="bbn-search-wrap">
                      <SearchIcon className="bbn-search-icon" sx={{ fontSize: 18 }} />
                      <input
                        className="search"
                        placeholder="Search topics…"
                        autoComplete="off"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") setSearch("");
                        }}
                      />
                      {search && (
                        <button
                          type="button"
                          className="search-clear"
                          onClick={() => setSearch("")}
                          aria-label="Clear search"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      className="cv-btn cv-btn-secondary bbn-shuffle"
                      onClick={shuffleTopics}
                      disabled={topicsLoading || topics.length === 0}
                    >
                      <ShuffleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "text-top" }} />
                      Shuffle
                    </button>
                  </div>
                </div>

                <div className="topics">
                  {topicsLoading
                    ? [1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} variant="rounded" height={52} sx={{ borderRadius: "12px" }} />
                      ))
                    : filteredTopics.map((topic) => (
                        <button
                          key={topic.id}
                          type="button"
                          className="topic"
                          onClick={() => handleTopicClick(topic)}
                        >
                          {topic.name}
                        </button>
                      ))}
                </div>

                {!topicsLoading && filteredTopics.length === 0 && (
                  <div className="hint">No topics found for this niche</div>
                )}

                {!topicsLoading && filteredTopics.length > 0 && (
                  <div className="hint">
                    <LightbulbOutlinedIcon sx={{ fontSize: 15, mr: 0.5, verticalAlign: "text-top" }} />
                    Click a topic to continue to format selection
                  </div>
                )}
              </div>
            )}

            {!loading && !selectedNiche && (
              <div className="hint bbn-empty-hint">
                Select a niche above to see topic ideas
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BrowseByNiche;
